import React, { useState, useEffect } from 'react';

interface WeeklyAvailability {
  AvailabilityId: string;
  TeamMemberId: string;
  WeekStartDate: string;
  AvailabilityPercent: number;
  Reason?: string;
}

interface WeeklyAvailabilityManagerProps {
  memberId: string;
  memberName: string;
  onSuccess?: () => void;
}

export default function WeeklyAvailabilityManager({
  memberId,
  memberName,
  onSuccess,
}: WeeklyAvailabilityManagerProps) {
  const [availabilities, setAvailabilities] = useState<WeeklyAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [availabilityPercent, setAvailabilityPercent] = useState(100);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAvailabilities();
  }, [memberId]);

  const fetchAvailabilities = async () => {
    try {
      setLoading(true);
      const nextYear = new Date();
      nextYear.setFullYear(nextYear.getFullYear() + 1);
      const fromDate = new Date().toISOString().slice(0, 10);
      const toDate = nextYear.toISOString().slice(0, 10);

      const res = await fetch(
        `/weekly-availability/${memberId}?from=${fromDate}&to=${toDate}`
      );
      const data = await res.json();

      if (data.success) {
        setAvailabilities(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch availabilities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAvailability = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!startDate || !endDate) {
      alert('Please select start and end dates');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/weekly-availability/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          memberId,
          startDate,
          endDate,
          availabilityPercent,
          reason,
          createdBy: localStorage.getItem('auth_email') || 'unknown',
        }),
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to set availability');
      }

      // Reset form
      setStartDate('');
      setEndDate('');
      setAvailabilityPercent(100);
      setReason('');
      setShowForm(false);

      // Refresh data
      await fetchAvailabilities();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAvailability = async (availabilityId: string) => {
    if (!confirm('Remove this availability override?')) return;

    try {
      const res = await fetch(`/weekly-availability/${availabilityId}`, {
        method: 'DELETE',
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete');
      }

      await fetchAvailabilities();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  if (loading) {
    return <div style={{ padding: '1rem' }}>Loading availability...</div>;
  }

  // Group availabilities by week
  const upcomingAvailabilities = availabilities
    .filter(a => new Date(a.WeekStartDate) >= new Date())
    .sort((a, b) => new Date(a.WeekStartDate).getTime() - new Date(b.WeekStartDate).getTime());

  return (
    <div>
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Weekly Availability - {memberName}</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: '0.5rem 1rem',
            background: '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {showForm ? 'Cancel' : '+ Add Time Off'}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleAddAvailability}
          style={{
            background: '#f9f9f9',
            padding: '1.5rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #ddd',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Availability: {availabilityPercent}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                step="10"
                value={availabilityPercent}
                onChange={e => setAvailabilityPercent(Number(e.target.value))}
                style={{ width: '100%' }}
              />
              <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                {availabilityPercent === 0
                  ? 'Completely unavailable (e.g., holiday)'
                  : availabilityPercent === 100
                    ? 'Fully available'
                    : `${availabilityPercent}% available for projects`}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Reason (optional)
              </label>
              <select
                value={reason}
                onChange={e => setReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                }}
              >
                <option value="">Select reason...</option>
                <option value="Holiday">Holiday</option>
                <option value="Training">Training</option>
                <option value="Conference">Conference</option>
                <option value="Sick Leave">Sick Leave</option>
                <option value="Personal">Personal</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? 'Saving...' : 'Save Availability'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setStartDate('');
                setEndDate('');
                setAvailabilityPercent(100);
                setReason('');
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {upcomingAvailabilities.length === 0 ? (
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            borderRadius: '4px',
            color: '#666',
          }}
        >
          <p>No time off scheduled</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Week Starting</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Availability</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Reason</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {upcomingAvailabilities.map(avail => (
                <tr key={avail.AvailabilityId} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.75rem' }}>
                    {new Date(avail.WeekStartDate).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td
                    style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      color: avail.AvailabilityPercent === 0 ? '#dc3545' : '#0066cc',
                    }}
                  >
                    {avail.AvailabilityPercent === 0 ? '❌ Unavailable' : `${avail.AvailabilityPercent}%`}
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>
                    {avail.Reason || '—'}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <button
                      onClick={() => handleDeleteAvailability(avail.AvailabilityId)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
